import React, { useState, useEffect, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import {
  Modal,
  Form,
  Table,
  InputNumber,
  Button,
  Space,
  Alert,
  Typography,
  Descriptions,
  Tag,
  Radio,
  Input,
  App,
  Spin,
  Select,
} from 'antd';
import { ExportOutlined, WarningOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useMaterialRequest } from '../../hooks/useMaterialRequest';
import { useIssueMaterials } from '../../hooks/useMaterialIssuance';
import { useWarehouses } from '../../hooks/useInventory';
import api from '@/services/api.service';
import {
  CriticalItemTag,
  getCriticalItemRowClassName,
  isItemCritical,
} from '@/components/inventory/criticalItemUi';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import { documentLineSize } from '@/utils/inventory/measurementQuantity';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface MaterialIssuanceDialogProps {
  visible: boolean;
  materialRequestId: string | null;
  onClose: () => void;
  onSuccess?: () => void;
}

interface IssuanceItem {
  material_request_item_id: string;
  item_id: string;
  item_code: string;
  item_name: string;
  uom: string;
  requested_quantity: number;
  base_quantity: number;
  area_dimension: number;
  issued_quantity: number;
  pending_quantity: number;
  reserved_quantity: number;
  /** Effective qty in UOM = pieces_to_issue × area_dimension (server recomputes). */
  quantity_to_issue: number;
  pieces_to_issue: number;
  pending_pieces: number;
  /** Inherited from the MR line (read-only) */
  dimensions_label: string;
  is_critical?: boolean;
  heat_number_mandatory?: boolean;
  heat_number?: string;
  dimension?: string;
}

const round4 = (n: number) => Math.round(n * 10000) / 10000;

function effectiveFromPieces(item: Pick<IssuanceItem, 'area_dimension' | 'pending_quantity' | 'pending_pieces'>, pieces: number): number {
  if (pieces >= item.pending_pieces) return item.pending_quantity;
  return round4(pieces * (item.area_dimension || 1));
}

function encodeHeatDimBucket(heat: string, dimension: string): string {
  return JSON.stringify({ h: heat.trim(), d: dimension.trim() });
}

function decodeHeatDimBucket(s: string): { heat: string; dimension: string } {
  try {
    const o = JSON.parse(s) as { h?: string; d?: string };
    return { heat: String(o.h ?? '').trim(), dimension: String(o.d ?? '').trim() };
  } catch {
    return { heat: '', dimension: '' };
  }
}

function needsIssuanceHeat(it: IssuanceItem): boolean {
  return Boolean(it.is_critical && it.heat_number_mandatory);
}

async function fetchStockByHeat(itemId: string, warehouseId: string) {
  const res = await api.get(`/inventory/items/${itemId}/stock-by-heat`, {
    params: { warehouse_id: warehouseId },
  });
  const body = (res as any)?.data?.data ?? (res as any)?.data;
  return Array.isArray(body) ? body : [];
}

export const MaterialIssuanceDialog: React.FC<MaterialIssuanceDialogProps> = ({
  visible,
  materialRequestId,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const [issuanceType, setIssuanceType] = useState<'Full' | 'Partial'>('Full');
  const [items, setItems] = useState<IssuanceItem[]>([]);

  const { data: materialRequest, isLoading } = useMaterialRequest(materialRequestId || '', {
    enabled: visible && !!materialRequestId,
  });

  const watchedWarehouseId = Form.useWatch('source_warehouse_id', form);
  const issueWarehouseId = materialRequest?.source_warehouse_id || watchedWarehouseId;

  const issueMaterialsMutation = useIssueMaterials();
  const { data: warehouses = [] } = useWarehouses();
  const { data: departments = [] } = useDepartmentsForSelection();

  const sourceWarehouseLabel = useMemo(() => {
    if (!materialRequest?.source_warehouse_id) return '';
    const w = warehouses.find((x) => x.id === materialRequest.source_warehouse_id);
    return w ? `${w.warehouse_code} — ${w.warehouse_name}` : materialRequest.source_warehouse_id;
  }, [materialRequest?.source_warehouse_id, warehouses]);

  const departmentLabel = useMemo(() => {
    if (!materialRequest?.department_id) return '';
    const d = departments.find((x) => x.id === materialRequest.department_id);
    return d ? `${d.department_code} - ${d.department_name}` : materialRequest.department_id;
  }, [materialRequest?.department_id, departments]);

  useEffect(() => {
    if (materialRequest && visible) {
      // Initialize items from material request
      const initialItems: IssuanceItem[] = (materialRequest.items || []).map(item => {
        const pendingQty = typeof item.pending_quantity === 'string' 
          ? parseFloat(item.pending_quantity) 
          : item.pending_quantity;
        const requestedQty = typeof item.requested_quantity === 'string'
          ? parseFloat(item.requested_quantity)
          : item.requested_quantity;
        const areaDimensionRaw = Number((item as { area_dimension?: number }).area_dimension ?? 1);
        const areaDimension = Number.isFinite(areaDimensionRaw) && areaDimensionRaw > 0 ? areaDimensionRaw : 1;
        const baseQtyRaw = Number((item as { base_quantity?: number }).base_quantity ?? 0);
        const baseQty = baseQtyRaw > 0 ? baseQtyRaw : requestedQty;
        const pendingPieces = areaDimension !== 1 ? round4(pendingQty / areaDimension) : pendingQty;
        return {
          material_request_item_id: item.id || '',
          item_id: item.item_id,
          item_code: item.item_code || '',
          item_name: item.item_name || '',
          uom: item.uom,
          requested_quantity: requestedQty,
          base_quantity: baseQty,
          area_dimension: areaDimension,
          issued_quantity: typeof item.issued_quantity === 'string' 
            ? parseFloat(item.issued_quantity) 
            : item.issued_quantity,
          pending_quantity: pendingQty,
          reserved_quantity: Number((item as { reserved_quantity?: number }).reserved_quantity ?? 0) || 0,
          quantity_to_issue: pendingQty, // Default to full pending quantity
          pieces_to_issue: pendingPieces,
          pending_pieces: pendingPieces,
          dimensions_label: documentLineSize(item),
          is_critical: Boolean(item.is_critical),
          heat_number_mandatory: Boolean(item.heat_number_mandatory),
          heat_number: String((item as { heat_number?: string }).heat_number ?? '').trim(),
          dimension: String((item as { dimension?: string }).dimension ?? '').trim(),
        };
      }).filter(item => item.pending_quantity > 0); // Only show items with pending quantity

      setItems(initialItems);
      form.resetFields();
    }
  }, [materialRequest, visible, form]);

  const handleQuantityChange = (materialRequestItemId: string, value: number | null) => {
    setItems((prev) =>
      prev.map((item) =>
        item.material_request_item_id === materialRequestItemId
          ? {
              ...item,
              pieces_to_issue: value || 0,
              quantity_to_issue: effectiveFromPieces(item, value || 0),
            }
          : item
      )
    );
  };

  const handleTraceabilityBucketChange = (
    materialRequestItemId: string,
    heat: string,
    dimension: string
  ) => {
    setItems((prev) =>
      prev.map((item) =>
        item.material_request_item_id === materialRequestItemId
          ? { ...item, heat_number: heat, dimension }
          : item
      )
    );
  };

  const stockByHeatQueries = useQueries({
    queries: items.map((it) => ({
      queryKey: ['stock-by-heat', it.item_id, issueWarehouseId],
      queryFn: () => fetchStockByHeat(it.item_id, String(issueWarehouseId)),
      enabled:
        Boolean(visible && materialRequest && issueWarehouseId && needsIssuanceHeat(it)),
    })),
  });

  const heatMetaByItemId = useMemo(() => {
    const m = new Map<string, { loading: boolean; options: { label: string; value: string }[] }>();
    items.forEach((it, idx) => {
      if (!needsIssuanceHeat(it)) return;
      const q = stockByHeatQueries[idx];
      const raw = Array.isArray(q.data) ? q.data : [];
      const opts = raw
        .filter(
          (r: { heat_number?: string; dimension?: string; available_quantity?: number }) => {
            const h = String(r.heat_number ?? '').trim();
            const d = String(r.dimension ?? '').trim();
            return h && d && Number(r.available_quantity) > 0;
          }
        )
        .map(
          (r: {
            heat_number?: string;
            dimension?: string;
            available_quantity?: number;
            mtc_document_id?: string | null;
            mtc_document_number?: string | null;
          }) => {
            const hn = String(r.heat_number).trim();
            const dim = String(r.dimension ?? '').trim();
            const mtcPart = r.mtc_document_number?.trim()
              ? ` — MTC ${r.mtc_document_number.trim()}`
              : r.mtc_document_id
                ? ' (MTC)'
                : '';
            return {
              value: encodeHeatDimBucket(hn, dim),
              label: `${hn} / ${dim} — free ${Number(r.available_quantity).toFixed(2)}${mtcPart}`,
            };
          }
        );
      const reqH = String(it.heat_number ?? '').trim();
      const reqD = String(it.dimension ?? '').trim();
      if (reqH && reqD) {
        const v = encodeHeatDimBucket(reqH, reqD);
        if (!opts.some((o) => o.value === v)) {
          opts.unshift({
            value: v,
            label: `${reqH} / ${reqD} — as requested (check qty)`,
          });
        }
      }
      m.set(it.item_id, { loading: Boolean(q.isLoading), options: opts });
    });
    return m;
  }, [items, stockByHeatQueries]);

  const handleIssuanceTypeChange = (type: 'Full' | 'Partial') => {
    setIssuanceType(type);
    if (type === 'Full') {
      // Reset all quantities to full pending
      setItems(prev => prev.map(item => ({
        ...item,
        quantity_to_issue: item.pending_quantity,
        pieces_to_issue: item.pending_pieces,
      })));
    }
  };

  const handleSubmit = async (values: any) => {
    if (!materialRequest) return;

    // Validate that at least one item has quantity
    const itemsToIssue = items.filter(item => item.quantity_to_issue > 0);
    if (itemsToIssue.length === 0) {
      message.error('Please specify quantity to issue for at least one item');
      return;
    }

    // Validate quantities don't exceed pending
    const invalidItems = itemsToIssue.filter(item => item.quantity_to_issue > item.pending_quantity);
    if (invalidItems.length > 0) {
      message.error('Quantity to issue cannot exceed pending quantity');
      return;
    }

    for (const item of itemsToIssue) {
      if (needsIssuanceHeat(item) && !String(item.heat_number ?? '').trim()) {
        message.error(`Select heat + dimension for critical item ${item.item_code || item.item_id}`);
        return;
      }
      if (needsIssuanceHeat(item) && !String(item.dimension ?? '').trim()) {
        message.error(`Select heat + dimension for critical item ${item.item_code || item.item_id}`);
        return;
      }
    }

    try {
      const whId = materialRequest.source_warehouse_id || values.source_warehouse_id;
      if (!whId || String(whId).trim() === '') {
        message.error('Source warehouse is required');
        return;
      }
      await issueMaterialsMutation.mutateAsync({
        material_request_id: materialRequest.id,
        issuance_type: issuanceType,
        source_warehouse_id: String(whId).trim(),
        department_id: materialRequest.department_id ?? undefined,
        notes: values.notes,
        items: itemsToIssue.map((item) => ({
          material_request_item_id: item.material_request_item_id,
          item_id: item.item_id,
          issued_quantity: item.quantity_to_issue,
          base_quantity: item.pieces_to_issue,
          uom: item.uom,
          heat_number: needsIssuanceHeat(item) ? String(item.heat_number ?? '').trim() : undefined,
          dimension: needsIssuanceHeat(item) ? String(item.dimension ?? '').trim() : undefined,
        })),
      });

      onSuccess?.();
      onClose();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to issue materials');
    }
  };

  const columns = useMemo(
    () => [
      {
        title: 'Item Code',
        dataIndex: 'item_code',
        width: 140,
        render: (code: string, record: IssuanceItem) => (
          <Space size={6} wrap>
            <span>{code}</span>
            <CriticalItemTag critical={isItemCritical(record)} />
          </Space>
        ),
      },
      {
        title: 'Item Name',
        dataIndex: 'item_name',
        width: 200,
      },
      {
        title: 'UOM',
        dataIndex: 'uom',
        width: 80,
      },
      {
        title: 'Size (L×W×H)',
        dataIndex: 'dimensions_label',
        width: 140,
        render: (v: string) => v || '—',
      },
      {
        title: 'Pending pcs',
        dataIndex: 'pending_pieces',
        width: 100,
        render: (qty: number, record: IssuanceItem) =>
          record.area_dimension !== 1 ? Number(qty).toLocaleString(undefined, { maximumFractionDigits: 4 }) : '—',
      },
      {
        title: 'Requested (Eff.)',
        dataIndex: 'requested_quantity',
        width: 130,
        render: (qty: number) => qty.toFixed(2),
      },
      {
        title: 'Already Issued (Eff.)',
        dataIndex: 'issued_quantity',
        width: 140,
        render: (qty: number) => <Text type="secondary">{qty.toFixed(2)}</Text>,
      },
      {
        title: 'Pending (Eff.)',
        dataIndex: 'pending_quantity',
        width: 120,
        render: (qty: number) => (
          <Text strong style={{ color: '#145A54' }}>
            {qty.toFixed(2)}
          </Text>
        ),
      },
      {
        title: 'Reserved',
        dataIndex: 'reserved_quantity',
        width: 100,
        render: (qty: number, record: IssuanceItem) => {
          const n = Number(qty) || 0;
          const issue = Number(record.quantity_to_issue) || 0;
          const over = issue > n && n > 0;
          return (
            <Text type={over ? 'danger' : n > 0 ? 'warning' : 'secondary'}>
              {n.toFixed(2)}
            </Text>
          );
        },
      },
      {
        title: 'Heat / dimension',
        key: 'heat',
        width: 280,
        render: (_: unknown, record: IssuanceItem) => {
          if (!needsIssuanceHeat(record)) return '—';
          if (!issueWarehouseId) {
            return (
              <Text type="secondary" style={{ fontSize: 12 }}>
                Set source warehouse
              </Text>
            );
          }
          const meta = heatMetaByItemId.get(record.item_id);
          if (meta?.loading) return <Spin size="small" />;
          const hn = String(record.heat_number ?? '').trim();
          const dim = String(record.dimension ?? '').trim();
          const selectValue =
            hn && dim ? encodeHeatDimBucket(hn, dim) : undefined;
          return (
            <Select
              showSearch
              allowClear
              placeholder="Heat + dimension"
              style={{ width: '100%' }}
              value={selectValue}
              onChange={(v) => {
                if (!v) {
                  handleTraceabilityBucketChange(record.material_request_item_id, '', '');
                  return;
                }
                const { heat, dimension } = decodeHeatDimBucket(v);
                handleTraceabilityBucketChange(record.material_request_item_id, heat, dimension);
              }}
              options={meta?.options ?? []}
              optionFilterProp="label"
              popupMatchSelectWidth={false}
              dropdownStyle={{
                minWidth: 360,
                maxWidth: 'min(560px, calc(100vw - 32px))',
              }}
              popupClassName="material-issuance-heat-select-dropdown"
              filterOption={(input, option) =>
                String(option?.label ?? '')
                  .toLowerCase()
                  .includes(input.trim().toLowerCase())
              }
            />
          );
        },
      },
      {
        title: 'Qty / Pieces to Issue',
        dataIndex: 'pieces_to_issue',
        width: 160,
        render: (_: unknown, record: IssuanceItem) => (
          <>
            <InputNumber
              min={0}
              max={record.pending_pieces}
              value={record.pieces_to_issue}
              onChange={(value) => handleQuantityChange(record.material_request_item_id, value)}
              precision={record.area_dimension !== 1 ? 4 : 2}
              style={{ width: '100%' }}
              disabled={issuanceType === 'Full'}
            />
            {record.area_dimension !== 1 ? (
              <Text type="secondary" style={{ fontSize: 11 }}>
                pcs → {record.quantity_to_issue.toLocaleString(undefined, { maximumFractionDigits: 4 })}{' '}
                {record.uom}
              </Text>
            ) : null}
          </>
        ),
      },
    ],
    [heatMetaByItemId, issueWarehouseId, issuanceType]
  );

  const totalToIssue = items.reduce((sum, item) => sum + item.quantity_to_issue, 0);

  return (
    <Modal
      title={
        <Space>
          <ExportOutlined />
          <Text strong>Issue Materials</Text>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={1000}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          icon={<ExportOutlined />}
          onClick={() => form.submit()}
          loading={issueMaterialsMutation.isPending}
          disabled={totalToIssue === 0}
        >
          Issue Materials
        </Button>,
      ]}
    >
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : materialRequest ? (
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {/* Request Info */}
          <Descriptions bordered column={2} size="small" style={{ marginBottom: 24 }}>
            <Descriptions.Item label="Request Number">
              <Text strong>{materialRequest.request_number}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Work Order">
              {materialRequest.work_order_number ? (
                <Text strong>{materialRequest.work_order_number}</Text>
              ) : (
                <Text type="secondary">—</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Request Date">
              {dayjs(materialRequest.request_date).format('DD MMM YYYY')}
            </Descriptions.Item>
            <Descriptions.Item label="Required Date">
              {materialRequest.required_date ? dayjs(materialRequest.required_date).format('DD MMM YYYY') : '-'}
            </Descriptions.Item>
            {materialRequest.department_id && (
              <Descriptions.Item label="Department">
                <Text>{departmentLabel}</Text>
              </Descriptions.Item>
            )}
            {materialRequest.source_warehouse_id && (
              <Descriptions.Item label="Source warehouse (stock deducted from)" span={2}>
                <Text>{sourceWarehouseLabel}</Text>
              </Descriptions.Item>
            )}
          </Descriptions>

          {/* Issuance Type */}
          <Form.Item label="Issuance Type" required style={{ marginBottom: 16 }}>
            <Radio.Group
              value={issuanceType}
              onChange={(e) => handleIssuanceTypeChange(e.target.value)}
            >
              <Radio.Button value="Full">Full Issuance</Radio.Button>
              <Radio.Button value="Partial">Partial Issuance</Radio.Button>
            </Radio.Group>
          </Form.Item>

          {issuanceType === 'Partial' && (
            <Alert
              message="Partial Issuance"
              description="Adjust quantities below to issue partial quantities. You can issue the remaining quantities in future issuances."
              type="info"
              showIcon
              icon={<WarningOutlined />}
              style={{ marginBottom: 16 }}
            />
          )}

          {/* Items Table */}
          <div style={{ marginBottom: 16 }}>
            <Title level={5}>Items to Issue</Title>
            <Table
              columns={columns}
              dataSource={items}
              rowKey="material_request_item_id"
              pagination={false}
              size="small"
              scroll={{ x: 1180 }}
              rowClassName={(r) => getCriticalItemRowClassName(r)}
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={10} align="right">
                      <Text strong>Total Items to Issue:</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>
                      <Text strong style={{ color: '#145A54' }}>
                        {totalToIssue.toFixed(2)}
                      </Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />
          </div>

          {/* Warehouse */}
          {!materialRequest.source_warehouse_id && (
            <Form.Item
              label="Source warehouse (must match where opening stock / GRN was posted)"
              name="source_warehouse_id"
              rules={[{ required: true, message: 'Please select the warehouse to issue from' }]}
            >
              <Select
                showSearch
                placeholder="Select warehouse"
                optionFilterProp="label"
                options={warehouses.map((w) => ({
                  value: w.id,
                  label: `${w.warehouse_code ?? w.id} — ${w.warehouse_name ?? ''}`,
                }))}
              />
            </Form.Item>
          )}

          {/* Notes */}
          <Form.Item label="Notes" name="notes">
            <TextArea rows={3} placeholder="Enter any notes for this issuance..." />
          </Form.Item>
        </Form>
      ) : (
        <Alert
          message="Material Request Not Found"
          description="The requested material request could not be found."
          type="error"
          showIcon
        />
      )}
    </Modal>
  );
};
